/**
 * Ordered blocks for one assistant turn.
 *
 * Mirrored in `server/src/store/blocks.ts`. The two packages do not share a
 * compile, so this file is duplicated on purpose — keep them identical. A
 * reload replays the same events the live stream already applied; if the
 * copies drift, the thread looks different after refresh than it did while
 * streaming.
 */

export type Block =
  | { kind: "text"; seq: number; text: string }
  | { kind: "thinking"; seq: number; text: string }
  | {
      kind: "tool";
      seq: number;
      toolId: string;
      name: string;
      status: "running" | "ok" | "failed";
      input: unknown;
      output: string;
    };

export type BlockEvent =
  | { type: "text"; delta: string }
  | { type: "thinking"; delta: string }
  | { type: "tool_start"; id: string; name: string; input?: unknown }
  | { type: "tool_input"; id: string; input: unknown }
  | { type: "tool_end"; id: string; ok: boolean; output?: string };

function nextSeq(blocks: Block[]): number {
  if (blocks.length === 0) return 0;
  let max = 0;
  for (const b of blocks) if (b.seq > max) max = b.seq;
  return max + 1;
}

/**
 * Append one engine/SSE event onto the block list.
 *
 * Text and thinking append to a trailing block of the same kind; anything else
 * at the tail (a tool call between two sentences) opens a fresh block. Tool
 * starts are deduped by id so a resumed stream cannot insert the same call twice.
 */
export function applyBlockEvent(blocks: Block[], evt: BlockEvent): Block[] {
  switch (evt.type) {
    case "text": {
      const last = blocks[blocks.length - 1];
      if (last?.kind === "text") {
        return [...blocks.slice(0, -1), { ...last, text: last.text + evt.delta }];
      }
      return [...blocks, { kind: "text", seq: nextSeq(blocks), text: evt.delta }];
    }
    case "thinking": {
      const last = blocks[blocks.length - 1];
      if (last?.kind === "thinking") {
        return [...blocks.slice(0, -1), { ...last, text: last.text + evt.delta }];
      }
      return [...blocks, { kind: "thinking", seq: nextSeq(blocks), text: evt.delta }];
    }
    case "tool_start": {
      if (blocks.some((b) => b.kind === "tool" && b.toolId === evt.id)) return blocks;
      return [
        ...blocks,
        {
          kind: "tool",
          seq: nextSeq(blocks),
          toolId: evt.id,
          name: evt.name,
          status: "running",
          input: evt.input,
          output: "",
        },
      ];
    }
    case "tool_input": {
      return blocks.map((b) =>
        b.kind === "tool" && b.toolId === evt.id ? { ...b, input: evt.input } : b,
      );
    }
    case "tool_end": {
      return blocks.map((b) =>
        b.kind === "tool" && b.toolId === evt.id
          ? { ...b, status: evt.ok ? "ok" : "failed", output: evt.output ?? b.output }
          : b,
      );
    }
    default:
      return blocks;
  }
}

/** A stopped/failed turn must not leave a tool row spinning. */
export function settleRunningTools(blocks: Block[]): Block[] {
  let changed = false;
  const next = blocks.map((b) => {
    if (b.kind !== "tool" || b.status !== "running") return b;
    changed = true;
    return { ...b, status: "failed" as const };
  });
  return changed ? next : blocks;
}
